import { useState, useEffect } from 'react';
import { useQuery, keepPreviousData, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, Shield } from 'lucide-react';
import { usersApi } from '@/api/users';
import { useAuth } from '@/context/AuthContext';
import { userSchema, type UserFormData } from '@/utils/validators';
import type { User } from '@/types';
import { z } from 'zod';
import DataTable from '@/components/DataTable';
import Modal from '@/components/Modal';
import ConfirmDialog from '@/components/ConfirmDialog';
import FormField from '@/components/FormField';
import Pagination from '@/components/Pagination';
import { formatDate, getStatusColor } from '@/utils/formatters';

const userEditSchema = z.object({
  name: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  email: z.string().email('Correo electrónico inválido'),
  password: z
    .string()
    .min(8, 'La contraseña debe tener al menos 8 caracteres')
    .regex(/[A-Z]/, 'Debe contener al menos una mayúscula')
    .regex(/[a-z]/, 'Debe contener al menos una minúscula')
    .regex(/[0-9]/, 'Debe contener al menos un número')
    .optional()
    .or(z.literal('')),
  role: z.enum(['admin', 'technician', 'viewer']),
  active: z.boolean().optional(),
});

type UserEditFormData = z.infer<typeof userEditSchema>;

const roleLabels: Record<string, string> = {
  admin: 'Admin',
  supervisor: 'Supervisor',
  technician: 'Técnico',
  viewer: 'Espectador',
};

const roleColors: Record<string, string> = {
  admin: 'text-red-600 bg-red-100',
  supervisor: 'text-blue-600 bg-blue-100',
  technician: 'text-blue-600 bg-blue-100',
  viewer: 'text-gray-600 bg-gray-100',
};

export function Users() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const limit = 10;

  const { data: response, isLoading, error } = useQuery({
    queryKey: ['users', page],
    queryFn: () => usersApi.getAll({ page, limit }),
    placeholderData: keepPreviousData,
  });

  const users: User[] = response?.data?.data || [];
  const totalPages = response?.data?.totalPages || 1;

  const isEditing = !!editingUser;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UserFormData | UserEditFormData>({
    resolver: zodResolver(isEditing ? userEditSchema : userSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      role: 'technician',
    },
  });

  useEffect(() => {
    if (editingUser) {
      reset({
        name: editingUser.name,
        email: editingUser.email,
        password: '',
        role: editingUser.role,
        active: editingUser.active,
      });
    } else {
      reset({
        name: '',
        email: '',
        password: '',
        role: 'technician',
      });
    }
  }, [editingUser, reset]);

  const createMutation = useMutation({
    mutationFn: (data: UserFormData) => usersApi.create(data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Usuario creado exitosamente');
      closeModal();
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Error al crear usuario');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UserEditFormData) => {
      const payload: Record<string, unknown> = { name: data.name, email: data.email, role: data.role };
      if (data.password) {
        payload.password = data.password;
      }
      if (data.active !== undefined) {
        payload.active = data.active;
      }
      return usersApi.update(editingUser!.id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Usuario actualizado exitosamente');
      closeModal();
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Error al actualizar usuario');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => usersApi.remove(deleteTarget!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Usuario eliminado exitosamente');
      setDeleteTarget(null);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Error al eliminar usuario');
    },
  });

  const openCreateModal = () => {
    setEditingUser(null);
    setModalOpen(true);
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingUser(null);
  };

  const onSubmit = (data: UserFormData | UserEditFormData) => {
    if (isEditing) {
      updateMutation.mutate(data as UserEditFormData);
    } else {
      createMutation.mutate(data as UserFormData);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const columns = [
    { key: 'name', label: 'Nombre', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    {
      key: 'role',
      label: 'Rol',
      sortable: true,
      render: (item: User) => {
        const role = item.role;
        const displayRole = roleLabels[role] || role;
        const color = roleColors[role] || 'text-gray-600 bg-gray-100';
        return (
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${color}`}>
            {displayRole}
          </span>
        );
      },
    },
    {
      key: 'active',
      label: 'Estado',
      sortable: true,
      render: (item: User) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(item.active ? 'active' : 'inactive')}`}>
          {item.active ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Creado',
      sortable: true,
      render: (item: User) => formatDate(item.createdAt),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (item: User) => (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openEditModal(item); }}
            className="rounded-md p-1.5 text-surface-400 hover:bg-surface-100 hover:text-primary-600 transition-colors"
            title="Editar usuario"
          >
            <Edit className="h-4 w-4" />
          </button>
          {item.id !== currentUser?.id && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setDeleteTarget(item); }}
              className="rounded-md p-1.5 text-surface-400 hover:bg-surface-100 hover:text-danger-600 transition-colors"
              title="Eliminar usuario"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const errorMessage = error
    ? typeof error === 'string'
      ? error
      : 'Error al cargar usuarios'
    : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-surface-400" />
          <h1 className="text-2xl font-bold text-surface-900">Usuarios</h1>
        </div>
        <button
          type="button"
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Nuevo Usuario
        </button>
      </div>

      <DataTable<User>
        columns={columns}
        data={users}
        loading={isLoading}
        error={errorMessage}
        emptyMessage="No hay usuarios registrados"
        onRowClick={(item) => openEditModal(item)}
      />

      {!isLoading && users.length > 0 && (
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      )}

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}
        size="lg"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormField label="Nombre" error={errors.name?.message} required>
            <input
              type="text"
              {...register('name')}
              className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="Nombre completo"
            />
          </FormField>

          <FormField label="Correo Electrónico" error={errors.email?.message} required>
            <input
              type="email"
              {...register('email')}
              className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="correo@ejemplo.com"
            />
          </FormField>

          <FormField
            label={isEditing ? 'Contraseña (dejar vacío para mantener)' : 'Contraseña'}
            error={errors.password?.message}
            required={!isEditing}
          >
            <input
              type="password"
              {...register('password')}
              className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder={isEditing ? 'Nueva contraseña (opcional)' : 'Contraseña'}
            />
          </FormField>

          <FormField label="Rol" error={errors.role?.message} required>
            <select
              {...register('role')}
              className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="admin">Admin</option>
              <option value="technician">Técnico</option>
              <option value="viewer">Espectador</option>
            </select>
          </FormField>

          {isEditing && (
            <FormField label="Activo">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  {...register('active')}
                  className="rounded border-surface-300 text-primary-600 shadow-sm focus:ring-primary-500"
                />
                <span className="text-sm text-surface-700">Usuario activo</span>
              </label>
            </FormField>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={closeModal}
              className="rounded-md border border-surface-300 bg-white px-4 py-2 text-sm font-medium text-surface-700 shadow-sm hover:bg-surface-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
            >
              {isSubmitting ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Guardando...
                </>
              ) : (
                isEditing ? 'Actualizar' : 'Crear'
              )}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate()}
        title="Eliminar Usuario"
        message={
          deleteTarget
            ? `¿Estás seguro de eliminar a ${deleteTarget.name}? Esta acción no se puede deshacer.`
            : ''
        }
        confirmText="Eliminar"
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}

export default Users;
